import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AppHolder, ThemedGlobalStyle } from './settings';
import { LanguageProvider } from './hooks/useLanguageSwitch';
import { darkTheme } from './settings/appStyle/theme';

import { Login } from './modules/AdminModules/AuthPages';
import { Dashboard } from './modules/AdminModules/Dashboard';

import AppContainer from './modules/conversationPages/appContainer/AppContainer';
import ReportViewContainer from './modules/conversationReport';
import LandingContainer from './modules/landingPages/landingContainer';
import RootLayout from './components/layout/RootLayout';
import { Invoices } from './modules/AdminModules/Invoices';
import { Invoice } from './modules/AdminModules/Invoice';

const queryClient = new QueryClient();

// ============================================
// Simple localStorage-based login check
// ============================================
const isLoggedIn = () => Boolean(localStorage.getItem('token'));

// ============================================
// Protected Route
// ============================================
const ProtectedRoute = () => {
  const location = useLocation();

  if (!isLoggedIn()) {
    return <Navigate to="/admin/portal" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

// ============================================
// Public Route
// ============================================
const PublicRoute = () => {
  if (isLoggedIn()) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

// ============================================
// Protected User Routes — everything a logged-in user sees at "/"
// ============================================
const UserRoutes = () => {
  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<RootLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="/invoices/:chatbotId" element={<Invoices />} />
          <Route path="/invoice/:invoiceId" element={<Invoice />} />
        </Route>
      </Route>
    </Routes>
  );
};

// ============================================
// Dynamic root handler — decides Dashboard vs Landing at "/"
// ============================================
const UserOrPublicRoot = () => {
  return (
    <AppHolder id="app-global">{isLoggedIn() ? <UserRoutes /> : <LandingContainer />}</AppHolder>
  );
};

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <ThemedGlobalStyle />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LanguageProvider>
            <Routes>
              {/* public landing-style pages, always the same regardless of login */}
              <Route
                path="/policy"
                element={
                  <AppHolder id="app-global">
                    <LandingContainer />
                  </AppHolder>
                }
              />
              <Route
                path="/terms"
                element={
                  <AppHolder id="app-global">
                    <LandingContainer />
                  </AppHolder>
                }
              />
              <Route
                path="/agentCode/:id"
                element={
                  <AppHolder id="app-global">
                    <AppContainer />
                  </AppHolder>
                }
              />
              <Route
                path="/report/:id"
                element={
                  <AppHolder id="app-global">
                    <ReportViewContainer />
                  </AppHolder>
                }
              />

              {/* login — public only, redirects to "/" if already logged in */}
              <Route element={<PublicRoute />}>
                <Route
                  path="/admin/portal"
                  element={
                    <AppHolder id="app-global">
                      <Login />
                    </AppHolder>
                  }
                />
              </Route>

              {/* "/" and any nested protected paths under it — Landing if logged out, Dashboard if logged in */}
              <Route path="/*" element={<UserOrPublicRoot />} />

              {/* fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LanguageProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
